import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Grid, Link as MuiLink, Button, Stack, useTheme } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';
import { whatsAppHref } from '../../constants/conversion';
import { SALES_EMAIL } from '../../constants/contact';

const Footer = () => {
  const theme = useTheme();
  const [footerData, setFooterData] = useState(null);

  // Fetch footer data from API
  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const response = await apiService.getFooter();
        if (response && response.success && response.footer) {
          setFooterData(response.footer);
        }
      } catch (error) {
        console.error('Error fetching footer data:', error);
        // Use default values if API fails
        setFooterData({
          company_name: 'Boldcomp Solutions',
          tagline: 'We build systems that save time and grow revenue.',
          description:
            'Custom software, AI, automation, and campaign tools for NGOs, SMEs, institutions, and teams in Kenya and East Africa. Message us — we answer fast.',
          email: SALES_EMAIL,
          phone: '0726642205',
          address: 'P.O. Box 85573–00200, Nairobi',
          quick_links: [
            { text: 'Services', path: '/services' },
            { text: 'Client work', path: '/portfolio' },
            { text: 'Courses', path: '/courses' },
            { text: 'Blogs', path: '/blogs' },
            { text: 'Request scope', path: '/request-quote' },
            { text: 'Contact', path: '/contact' },
            { text: 'Careers', path: '/careers' },
          ],
          copyright_text: '© 2026 Boldcomp Solutions. All rights reserved.',
        });
      }
    };
    fetchFooterData();
  }, []);

  // Use dynamic data or fallback to defaults
  const companyName = footerData?.company_name || 'Boldcomp Solutions';
  const tagline = footerData?.tagline || 'We build systems that save time and grow revenue.';
  const description =
    footerData?.description ||
    'Custom software, AI, automation, and campaign tools for NGOs, SMEs, and institutions in Kenya and East Africa. Message us — we answer fast.';
  const email = footerData?.email || SALES_EMAIL;
  const phone = footerData?.phone || '0726642205';
  const address = footerData?.address || 'P.O. Box 85573–00200, Nairobi';
  const quickLinks = footerData?.quick_links || [
    { text: 'Services', path: '/services' },
    { text: 'Client work', path: '/portfolio' },
    { text: 'Courses', path: '/courses' },
    { text: 'Blogs', path: '/blogs' },
    { text: 'Request scope', path: '/request-quote' },
    { text: 'Contact', path: '/contact' },
    { text: 'Careers', path: '/careers' },
  ];
  const copyrightText = footerData?.copyright_text || '© 2026 Boldcomp Solutions. All rights reserved.';
  
  const footHead = {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.55)',
    mb: 2,
  };

  return (
    <Box
      sx={{
        background: 'linear-gradient(180deg, #16184a 0%, #12133a 100%)',
        color: 'white',
        py: { xs: 5, md: 6 },
        mt: 0,
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="center"
          alignItems="center"
          sx={{ mb: 5 }}
        >
          <Button
            component={Link}
            to="/request-quote"
            variant="contained"
            color="secondary"
            size="medium"
            sx={{ fontWeight: 700, color: 'primary.main', minWidth: 200 }}
          >
            Get a free project scope
          </Button>
          <Button
            component="a"
            href={whatsAppHref('Hi Boldcomp — I found you on the website and want to talk.')}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            size="medium"
            sx={{
              borderColor: 'rgba(255,255,255,0.35)',
              color: 'white',
              fontWeight: 600,
              '&:hover': { borderColor: 'rgba(255,255,255,0.6)', bgcolor: 'rgba(255,255,255,0.06)' },
            }}
          >
            WhatsApp
          </Button>
          <Button component={Link} to="/courses" variant="text" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
            Courses
          </Button>
          <Button component={Link} to="/blogs" variant="text" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
            Insights
          </Button>
        </Stack>
        <Grid container spacing={{ xs: 4, md: 5 }}>
          <Grid item xs={12} md={4}>
            <Typography sx={footHead}>Company</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 1.5 }}>
              {companyName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.85)', lineHeight: 1.65 }}>
              {tagline}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.65 }}>
              {description}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography sx={footHead}>Contact</Typography>
            <Stack spacing={1.75}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <EmailOutlinedIcon sx={{ fontSize: 20, opacity: 0.7, mt: 0.25 }} />
                <MuiLink href={`mailto:${email}`} color="inherit" sx={{ fontSize: '0.875rem', wordBreak: 'break-word' }}>
                  {email}
                </MuiLink>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <PhoneOutlinedIcon sx={{ fontSize: 20, opacity: 0.7, mt: 0.25 }} />
                <MuiLink href={`tel:${String(phone).replace(/\s/g, '')}`} color="inherit" sx={{ fontSize: '0.875rem' }}>
                  {phone}
                </MuiLink>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <LocationOnOutlinedIcon sx={{ fontSize: 20, opacity: 0.7, mt: 0.25 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.55 }}>
                  {address}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography sx={footHead}>Site map</Typography>
            <Stack spacing={0.75}>
              {quickLinks.map((link, index) => (
                <MuiLink
                  key={index}
                  component={Link}
                  to={link.path}
                  underline="hover"
                  color="inherit"
                  sx={{
                    fontSize: '0.875rem',
                    color: 'rgba(255,255,255,0.8)',
                    fontWeight: 500,
                    '&:hover': { color: 'white' },
                  }}
                >
                  {link.text}
                </MuiLink>
              ))}
            </Stack>
          </Grid>
        </Grid>

        <Box
          sx={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            pt: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8125rem' }}>
            {copyrightText}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;